<template>
    <div>
        <h5 v-if="showPrice" class="text-center">{{ $t('Summary') }}</h5>
        <div class="col-md-10 col-lg-8 col-xl-6 offset-md-1 offset-lg-2 offset-xl-3">
            <template v-if="showPrice">
                <div v-if="quote" class="row py-2">
                    <div class="col-3 font-weight-bold">{{ $t('Service') }}</div>
                    <div class="col-6">{{ renderService() }}</div>
                    <div class="col-3 text-right">{{ formatCurrency(fare.price) }}</div>
                </div>
                <div v-if="insurance" class="row py-2">
                    <div class="col-3 font-weight-bold">{{ $t('Insurance') }}</div>
                    <div class="col-6">{{ renderInsurance() }}</div>
                    <div class="col-3 text-right">{{ formatCurrency(insurance.cost) }}</div>
                </div>
                <div v-if="vat" class="row py-2">
                    <div class="col-3 font-weight-bold">{{ $t('VAT') }}</div>
                    <div class="col-6">{{ renderPercent(this.sales.vat) }}</div>
                    <div class="col-3 text-right">{{ formatCurrency(vat) }}</div>
                </div>
                <div v-if="total" class="row py-2 border-top font-weight-bold">
                    <div class="col-3">{{ $t('Total') }}</div>
                    <div class="col-9 text-right">{{ formatCurrency(total) }}</div>
                </div>
            </template>
            <div v-if="total" class="py-2">
                <div class="row">
                    <div class="col">
                        <div class="form-group form-check">
                            <input id="terms" v-model="terms" class="form-check-input" type="checkbox" required>
                            <i18n path="I agree to the {terms_of_service}, {transport_of_goods_policy} and {returns_policy}" tag="label" class="form-check-label" for="terms">
                                <template v-slot:terms_of_service>
                                    <a href="/legal/terms" target="_blank">{{ $t('Terms of Service') }}</a>
                                </template>
                                <template v-slot:transport_of_goods_policy>
                                    <a href="/legal/goods" target="_blank">{{ $t('Transport of goods Policy') }}</a>
                                </template>
                                <template v-slot:returns_policy>
                                    <a href="/legal/returns" target="_blank">{{ $t('Returns Policy') }}</a>
                                </template>
                            </i18n>
                        </div>
                    </div>
                </div>
                <div v-if="offline" class="row">
                    <div class="col">
                        <div class="alert alert-info">
                            <i class="fa fa-info-circle mr-2"/>
                            {{ $t('Shipping cost will be invoiced directly to the organisation.') }}
                        </div>
                        <div class="text-center">
                            <button :disabled="!terms" class="btn btn-primary" @click="createOrder">{{ $t('Proceed to shipment') }}</button>
                        </div>
                    </div>
                </div>
                <div v-else class="row">
                    <div class="col text-center">
                        <button :disabled="!terms" class="btn btn-primary" @click="createOrder">{{ $t('Pay') }} {{ formatCurrency(total) }}</button>
                        <pay ref="pay" :order-id="orderId" :trigger="!!orderId"></pay>
                    </div>
                </div>
            </div>
        </div>
        <div id="errorsModal" class="modal fade">
            <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title text-danger">
                            <i class="fa fa-exclamation-triangle mr-2"></i>{{ $t('Error') }}
                        </h5>
                        <button type="button" class="close" data-dismiss="modal" :aria-label="$t('Close')">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        {{ $t('Please fix the following error before continuing') }}
                        <ul class="text-danger">
                            <li v-for="field in errors">
                                {{ field[0] }}
                            </li>
                        </ul>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-dismiss="modal">{{ $t('Close') }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Pay from "../Pay";

export default {
    name: "PaymentForm",
    components: {Pay},
    props: {
        offline: {type: Boolean},
        showPrice: {type: Boolean},
    },
    data() {
        return {
            sales: {
                currency: process.env.MIX_SALES_CURRENCY,
                vat: process.env.MIX_SALES_VAT,
            },
            orderId: undefined,
            terms: false,
            errors: {},
        };
    },
    computed: {
        quote() {
            return this.$store.state.order.quote.quote;
        },
        service() {
            return this?.quote?.service;
        },
        fare() {
            return this.quote?.fare;
        },
        transit() {
            return this.quote?.transit;
        },
        insurance() {
            return {
                insured: this.$store.state.order.insurance.insured,
                amount: this.$store.state.order.insurance.amount,
                cost: this.$store.getters['order/insurance/cost'] ?? 0,
            }
        },
        vat() {
            return this.fare
                ? (this.fare.price + this.insurance.cost) * this.sales.vat
                : undefined;
        },
        total() {
            return this.fare
                ? this.fare.price + this.insurance.cost + this.vat
                : undefined;
        },
    },
    methods: {
        formatCurrency(amount) {
            return Intl.NumberFormat(
                document.body.dataset.locale,
                {style: 'currency', currency: this.sales.currency}
            ).format(amount);
        },
        renderPercent(value) {
            return Intl.NumberFormat(
                document.body.dataset.locale,
                {style: 'percent'}
            ).format(value);
        },
        renderService() {
            return this.transit ? `${this.service.name} (${this.transit.duration})` : this.service.name;
        },
        renderInsurance() {
            return this.insurance.insured && this.insurance.amount
                ? this.$t('Up to') + ' ' + this.formatCurrency(this.insurance.amount)
                : this.$t('No');
        },
        createOrder() {
            api.order.store(this.$store.getters['order/form'])
                .then(response => this.payOrder(response.data))
                .catch(this.processError);
        },
        payOrder(orderId) {
            this.orderId = orderId;
            if (this.offline) {
                location.href = `/order/${orderId}/pay/success`;
            }
        },
        processError(error) {
            if (error.response && error.response.data && error.response.data.errors) {
                this.errors = error.response.data.errors;
                $('#errorsModal').modal();
            } else if (error.response && error.response.data && error.response.data.message) {
                alert(error.response.data.message);
            } else {
                alert(error.message);
            }
        },
    }
}
</script>
